import { useMutation } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { signAsset } from '@/api/assets';
import { uploadSignedAsset } from '@/api/misc';
import { putInvoiceDocuments } from '@/api/Sherpa/Draw';
import { notifyUser } from '@/store/Toast';
import { sanitizeResponseError } from '@/utils';

type UploadTemplateData = {
  templateFile: File[];
  projectId: number;
  drawId: number;
  invoiceId: number;
};

type SignedFile = {
  fKey: string;
  url: string;
};

type UploadTemplateResult = {
  signedFiles: SignedFile[];
  uploadedFiles: any[];
  files: File[];
};

export type UploadTemplatesParam = {
  invoice?: UploadTemplateData;
  additionalDoc?: UploadTemplateData;
};

type UploadTemplatesFunction = (param: UploadTemplatesParam) => Promise<void>;

interface IUseInvoiceFileUpload {
  uploadTemplates: UploadTemplatesFunction;
  isLoading: boolean;
  uploadSuccess: boolean;
  isError: boolean;
}

const mapAwsUploadedFileToAsset = (result: UploadTemplateResult) =>
  result.uploadedFiles.map((_, index: number) => {
    return {
      name: result.files[index].name,
      key: result.signedFiles[index].fKey,
      contentType: result.files[index].type,
      size: result.files[index].size,
    };
  });

export const useInvoiceFileUpload = (): IUseInvoiceFileUpload => {
  const projectIdRef = useRef<number | undefined>(undefined);
  const drawIdRef = useRef<number | undefined>(undefined);
  const invoiceIdRef = useRef<number | undefined>(undefined);
  const templateFileRef = useRef<File[] | undefined>(undefined);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const updateInvoice = useMutation(
    (payload: { data: any; drawId: number; projectId: number; invoiceId: number }) => putInvoiceDocuments({ ...payload }),
    {
      onSuccess: () => {
        setIsLoading(false);
        setUploadSuccess(true);
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        setIsLoading(false);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const uploadTemplate = useCallback(async ({ templateFile, projectId, drawId, invoiceId }: UploadTemplateData) => {
    templateFileRef.current = templateFile;
    projectIdRef.current = projectId;
    drawIdRef.current = drawId;
    invoiceIdRef.current = invoiceId;

    const signPayload = templateFile.map<{
      key: string;
      type: string;
      resource: string;
    }>((file) => ({
      key: file.name,
      type: file.type,
      resource: 'projectDrawInvoice',
    }));

    const signedFiles = await Promise.all(signPayload.map((payload) => signAsset(payload)));

    const params = {
      sign: signedFiles,
      files: templateFileRef.current!,
    };

    const uploadedFiles = await Promise.all(params.sign.map(({ url }, index) => uploadSignedAsset({ file: params.files[index], url })));

    return {
      signedFiles,
      uploadedFiles,
      files: params.files,
    };
  }, []);

  const uploadTemplates: UploadTemplatesFunction = async (param) => {
    let invoiceRes: UploadTemplateResult | null = null;
    let additionalDocRes: UploadTemplateResult | null = null;
    setIsLoading(true);

    if (param.invoice) {
      invoiceRes = await uploadTemplate(param.invoice);
    }

    if (param.additionalDoc) {
      additionalDocRes = await uploadTemplate(param.additionalDoc);
    }

    const data = {
      ...(invoiceRes && {
        invoices: mapAwsUploadedFileToAsset(invoiceRes),
      }),
      ...(additionalDocRes && {
        additionalDocs: mapAwsUploadedFileToAsset(additionalDocRes),
      }),
    };

    updateInvoice.mutate({
      data,
      drawId: drawIdRef.current!,
      projectId: projectIdRef.current!,
      invoiceId: invoiceIdRef.current!,
    });
  };

  return {
    uploadTemplates,
    isLoading: isLoading,
    uploadSuccess: uploadSuccess,
    isError: updateInvoice.isError,
  };
};
